import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { FaqList, PageHero, Prose } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  FEES,
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  MICHAEL_PHONE_HREF,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
  PEOPLE,
  faqSchema,
} from "@/lib/firm";

const SITE = "https://coastsidelaw.com.au";
const CANONICAL = `${SITE}/scarborough-office`;
const GOLDEN_BEACH = `${SITE}/wills-and-probate-golden-beach`;

const TITLE = "Coastside Law Scarborough Office — Property, Wills & Estates | Fortune Street";
const DESCRIPTION = `Coastside Law's Scarborough office at ${PENINSULA_ADDRESS}: solicitor-led conveyancing (sales ${FEES.sale}, purchases ${FEES.purchase} incl. GST), wills, enduring powers of attorney, probate and elder law for the Redcliffe peninsula. Call Michael Klein on ${MICHAEL_PHONE}.`;

const michael = PEOPLE[0];
const michaelPhoto = typeof michael.photo === "string" ? michael.photo : String(michael.photo);

/**
 * Premises entity page for the second office. It states Scarborough as a real
 * branch of the same firm — same name, same phone, same solicitors — and links
 * to the Golden Beach entity page so Google reads one firm with two locations
 * rather than two unrelated businesses.
 */
const ANSWER =
  `${FIRM.name} has an office at ${PENINSULA_ADDRESS}, on the Redcliffe peninsula, seen by appointment. ` +
  `It is a branch of the same Queensland law firm whose principal office is at ${FIRM_ADDRESS} — the same name, the same phone numbers and the same admitted solicitors, not a separate business. ` +
  `From Scarborough we handle residential conveyancing at fixed fees of ${FEES.sale} for a sale and ${FEES.purchase} for a purchase, both inclusive of GST with free pre-signing contract review, along with wills from $660, will and enduring power of attorney packs from $825, probate and estate administration, body corporate and unit questions, elder law and business sales. ` +
  `Legal Practice Director Michael Klein was admitted in 2003, was managing partner of GKS Law for many years, lives on the peninsula and will come to you at home, in hospital or in aged care. ` +
  `Queensland conveyancing settles electronically through PEXA, so nothing requires you to attend an office. Call ${MICHAEL_PHONE} or email ${FIRM.email}.`;

const VARIANTS: ReadonlyArray<string> = [
  "Coastside Law Scarborough",
  "solicitor Scarborough QLD 4020",
  "conveyancing lawyer Scarborough Redcliffe",
  "wills solicitor Scarborough",
  "probate lawyer Redcliffe peninsula",
  "law firm Fortune Street Scarborough",
  "lawyer near me Scarborough Queensland",
  "conveyancer Scarborough Moreton Bay",
  "estate lawyer Redcliffe Scarborough",
  "solicitor home visit Scarborough",
];

const FACTS: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Entity", value: `${FIRM.legalName} — law firm, solicitor-led` },
  { label: "This office", value: `${PENINSULA_ADDRESS} — ${PENINSULA_OFFICE.note.toLowerCase()}` },
  { label: "Principal office", value: `${FIRM_ADDRESS} — open to the public` },
  {
    label: "Area served",
    value:
      "Scarborough, Redcliffe, Woody Point, Margate, Clontarf, Rothwell, Newport, Kippa-Ring, Deception Bay, North Lakes and the wider Moreton Bay region",
  },
  {
    label: "Services",
    value:
      "Conveyancing, wills, enduring powers of attorney, advance health directives, probate and estate administration, estate disputes, body corporate, elder law, business sales and leases",
  },
  { label: "Solicitor", value: `${michael.name}, ${michael.role} — admitted ${michael.admitted}` },
  { label: "Phone", value: `${MICHAEL_PHONE} (Michael Klein) · ${FIRM.phone} (Golden Beach)` },
  { label: "Email", value: FIRM.email },
  { label: "Professional body", value: FIRM.membership },
  { label: "Hours", value: FIRM.hours },
  { label: "Home, hospital and aged care visits", value: "Yes, across the Redcliffe peninsula" },
];

const SERVICES: ReadonlyArray<{ name: string; price: string; body: string; to: string }> = [
  {
    name: "Sale of a peninsula home or unit",
    price: `${FEES.sale} ${FEES.saleNote}`,
    body: "Contract drafting and review, disclosure, body corporate records, discharge of mortgage and PEXA settlement, at a fee agreed before we start.",
    to: "/conveyancing/scarborough",
  },
  {
    name: "Purchase",
    price: `${FEES.purchase} incl. GST`,
    body: "Free pre-signing contract review, cooling-off, finance and building and pest dates diarised, searches ordered and settlement booked through PEXA.",
    to: "/conveyancing/scarborough",
  },
  {
    name: "Will",
    price: "from $660 incl. GST",
    body: "A solicitor-drafted will covering the home, bank, super and personal assets, signed and witnessed correctly, with free safe custody of the original.",
    to: "/scarborough-wills-and-estates",
  },
  {
    name: "Will and enduring power of attorney pack",
    price: "$825 incl. GST",
    body: "A will plus an enduring power of attorney covering financial and personal or health decisions — the pairing that matters most from the seventies on.",
    to: "/scarborough-wills-and-estates",
  },
  {
    name: "Probate and estate administration",
    price: "Fixed fee, quoted upfront",
    body: "Supreme Court of Queensland probate applications, advertising, asset collection and distribution, with filing fees and advertising at cost.",
    to: "/scarborough-wills-and-estates",
  },
];

const FAQS = [
  {
    q: "Where exactly is the Scarborough office?",
    a: `${PENINSULA_ADDRESS}. It is seen by appointment, including evenings and weekends where that suits you better, and Michael Klein will come to you at home, in hospital or in aged care anywhere on the peninsula.`,
  },
  {
    q: "Is the Scarborough office a different firm from the Golden Beach one?",
    a: `No. It is the same firm — ${FIRM.name} — with a principal office at ${FIRM_ADDRESS} and this second office at ${PENINSULA_ADDRESS}. Same solicitors, same fixed fees, same file system.`,
  },
  {
    q: "Do I have to come into the office?",
    a: "No. Queensland conveyancing settles electronically through PEXA, contracts are signed digitally and identity can be verified by video. Wills can be signed at your kitchen table. The office is there if you would rather sit down with someone.",
  },
  {
    q: "What does a sale or purchase cost from the Scarborough office?",
    a: `Sales are ${FEES.sale} and purchases ${FEES.purchase}, both inclusive of GST, with pre-signing contract review free of charge. Complex or commercial matters are quoted before any work starts.`,
  },
  {
    q: "Who will I be dealing with?",
    a: `${michael.name}, ${michael.role}. He was admitted in ${michael.admitted}, is admitted to the ${michael.courts.join(" and the ")}, lives on the peninsula and spent many years as managing partner of GKS Law. His direct line is ${MICHAEL_PHONE}.`,
  },
  {
    q: "Do you act on units and body corporate matters?",
    a: "Yes. Buying into an esplanade scheme, by-laws, levies and recovery, committee decisions and disputes are everyday work on the peninsula, and we deal with them here rather than referring them to town.",
  },
  {
    q: "Can you help if the person who died lived on the peninsula but the family is elsewhere?",
    a: "Yes. Probate and estate administration are run remotely as a matter of course — documents are signed electronically or by post and executors anywhere in Australia can instruct us.",
  },
];

const legalServiceSchemaNode = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": `${CANONICAL}#legalservice`,
  name: FIRM.name,
  url: CANONICAL,
  telephone: MICHAEL_PHONE,
  email: FIRM.email,
  description: DESCRIPTION,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: PENINSULA_OFFICE.street,
    addressLocality: PENINSULA_OFFICE.suburb,
    addressRegion: PENINSULA_OFFICE.state,
    postalCode: PENINSULA_OFFICE.postcode,
    addressCountry: PENINSULA_OFFICE.country,
  },
  areaServed: [
    "Scarborough QLD",
    "Redcliffe QLD",
    "Woody Point QLD",
    "Margate QLD",
    "Clontarf QLD",
    "Rothwell QLD",
    "North Lakes QLD",
    "Moreton Bay Region",
  ],
  memberOf: { "@type": "Organization", name: "Queensland Law Society" },
  parentOrganization: { "@id": `${SITE}/#organization` },
  branchOf: { "@id": `${SITE}/#organization` },
  employee: [
    {
      "@type": "Person",
      name: michael.name,
      jobTitle: michael.role,
      image: {
        "@type": "ImageObject",
        url: `${SITE}${michaelPhoto.startsWith("/") ? "" : "/"}${michaelPhoto.replace(/^\//, "")}`,
        caption: `${michael.name}, ${michael.role}, ${FIRM.name}`,
      },
      telephone: MICHAEL_PHONE,
      knowsAbout: michael.areas,
      memberOf: { "@type": "Organization", name: "Queensland Law Society" },
    },
  ],
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  url: CANONICAL,
  name: TITLE,
  description: DESCRIPTION,
  about: { "@id": `${CANONICAL}#legalservice` },
  isPartOf: { "@id": `${SITE}/#website` },
  significantLink: [GOLDEN_BEACH, `${SITE}/conveyancing/scarborough`, `${SITE}/scarborough-wills-and-estates`],
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE },
    { "@type": "ListItem", position: 2, name: "Scarborough office", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/scarborough-office")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(legalServiceSchemaNode) },
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: ScarboroughOffice,
});

function ScarboroughOffice() {
  return (
    <>
      <PageHero
        eyebrow="Scarborough office"
        title="Coastside Law — Scarborough, Redcliffe peninsula"
        lead={`${PENINSULA_ADDRESS}, by appointment. The same firm as our Golden Beach principal office, with Legal Practice Director Michael Klein on ${MICHAEL_PHONE}.`}
      />

      <section className="mx-auto max-w-5xl px-5 pt-12">
        <div className="rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-xl text-foreground">In short</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{ANSWER}</p>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground/80">
            Also searched as: {VARIANTS.join(", ")}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="font-serif text-2xl text-foreground">Office and firm details</h2>
        <dl className="mt-6 divide-y divide-border border-y border-border">
          {FACTS.map((fact) => (
            <div key={fact.label} className="grid gap-1 py-4 sm:grid-cols-[220px_1fr] sm:gap-6">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{fact.label}</dt>
              <dd className="text-sm leading-relaxed text-foreground">{fact.value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
          Both premises belong to the one firm. The Golden Beach entity page is here:{" "}
          <Link to="/wills-and-probate-golden-beach" className="text-primary underline underline-offset-4">
            Coastside Law, 40 Theresa Street, Golden Beach
          </Link>
          . Our professional credentials are set out on the{" "}
          <Link to="/queensland-law-society" className="text-primary underline underline-offset-4">
            Queensland Law Society membership page
          </Link>
          .
        </p>
        <p className="mt-3 text-sm">
          <a href={MICHAEL_PHONE_HREF} className="font-medium text-primary underline underline-offset-4">
            Call Michael on {MICHAEL_PHONE}
          </a>
        </p>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-2xl text-foreground">What we do from Scarborough</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {SERVICES.map((service) => (
              <div key={service.name} className="rounded-sm border border-border bg-card p-6">
                <h3 className="font-serif text-lg text-foreground">{service.name}</h3>
                <p className="mt-1 text-sm font-medium text-primary">{service.price}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{service.body}</p>
                <Link
                  to={service.to}
                  className="mt-4 inline-block text-sm font-medium text-primary underline underline-offset-4"
                >
                  More detail
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <Prose>
          <h2 className="font-serif text-2xl text-foreground">Working on the peninsula</h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Scarborough, Redcliffe and Woody Point are unit and townhouse country, so most files here involve a body
            corporate: disclosure statements, levies, sinking fund forecasts and by-laws that decide whether the boat
            or the pet can stay. Older waterfront homes bring their own issues — unapproved structures, pool
            certificates and easements along the esplanade — and estates on the peninsula often involve a home held
            as tenants in common or a granny flat arrangement made years ago without documents. All of it is handled
            here, by a solicitor, on a fee agreed before we start.
          </p>
        </Prose>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-14">
        <ReviewStrip />
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-5 py-14">
          <h2 className="font-serif text-2xl text-foreground">Scarborough questions</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <EnquiryForm heading="Ask Michael about your Scarborough matter" suburb={PENINSULA_OFFICE.suburb} />
      </section>
    </>
  );
}
